import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { getAdminClient } from "@/lib/supabase-admin";
import { handleApiError } from "@/lib/errors";

const AdminLogin = () => {
  const { loading, user } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!loading && user) {
    return <Navigate to="/admin" replace />;
  }

  async function handleMagicLink(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { error } = await getAdminClient().auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/admin/auth-callback`,
        },
      });
      if (error) throw error;
      setSent(true);
    } catch (err) {
      handleApiError(err, "Kunne ikke sende innloggingslenken. Prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePassword(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    try {
      const { error } = await getAdminClient().auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      // useAdminAuth picks up the session via onAuthStateChange and AdminLayout
      // routes us to /admin (or the no-access screen).
    } catch (err) {
      handleApiError(err, "Feil e-post eller passord.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm space-y-6 rounded-lg border bg-card p-8 shadow-sm">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold">Logg inn</h1>
          <p className="text-sm text-muted-foreground">
            Admin-portal for Håp Naturlab.
          </p>
        </div>

        {sent ? (
          <div className="space-y-4">
            <div className="rounded-md bg-muted/50 p-4 text-center text-sm">
              <p className="font-medium">Sjekk e-posten din</p>
              <p className="mt-1 text-muted-foreground">
                Vi sendte en innloggingslenke til <span className="font-medium">{email}</span>.
                Lenken er gyldig i én time.
              </p>
            </div>
            <Button variant="ghost" className="w-full" onClick={() => setSent(false)}>
              Tilbake
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="password" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password">Passord</TabsTrigger>
              <TabsTrigger value="magic">E-postlenke</TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="mt-4">
              <form onSubmit={handlePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-pw">E-post</Label>
                  <Input
                    id="email-pw"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="navn@firma.no"
                    disabled={submitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passord</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Logger inn …" : "Logg inn"}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  Mangler passord? Logg inn med e-postlenke første gang og sett passord
                  via Supabase eller "Glemt passord"-flyten.
                </p>
              </form>
            </TabsContent>

            <TabsContent value="magic" className="mt-4">
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email-magic">E-post</Label>
                  <Input
                    id="email-magic"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="navn@firma.no"
                    disabled={submitting}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Sender …" : "Send innloggingslenke"}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  Magic link fungerer best på prod-domenet. Bruk passord på preview.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
